import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Save, Sparkles, TriangleAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ApiError } from '../lib/api';
import { BLOCKER_LABEL, callAiApi, type AiSettings, type AiSettingsInput } from '../lib/call-ai';
import { formatDateTime, formatNumber } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { Button, Card, CardHeader, Checkbox, ConfirmDialog, ErrorState, Field, Input, LoadingBlock, Metric, Select, Textarea, useToast } from './ui';

/**
 * Settings › Calls › AI summary (the operator's CR, 2026-10-29): an automatic transcript and summary of
 * every NEW call — switched on only on a server that is ready (CALLS_AI_ENABLED, the provider's key,
 * ffmpeg, the models), and only once the administrator confirms the people were told and it was
 * approved. Calls made before never are. The language, the models the server offers, retries, how many
 * at once, the words to expect, and the prices the usage report estimates from; then the usage itself.
 */
type Draft = Omit<AiSettingsInput, 'priceTranscriptionPerMinuteUsd' | 'priceSummaryInputPerMillionUsd' | 'priceSummaryOutputPerMillionUsd' | 'keywords' | 'maxConcurrentJobs'> & {
  keywords: string;
  maxConcurrentJobs: string;
  priceTranscription: string;
  priceInput: string;
  priceOutput: string;
};

const asText = (n: number | null) => (n === null ? '' : String(n));
const asPrice = (s: string): number | null => (s.trim() === '' ? null : Number(s));

function draftOf(s: AiSettings): Draft {
  return {
    enabled: s.enabled,
    summaryLanguage: s.summaryLanguage,
    transcriptionModel: s.transcriptionModel,
    summaryModel: s.summaryModel,
    retryFailedJobs: s.retryFailedJobs,
    maxConcurrentJobs: String(s.maxConcurrentJobs),
    keywords: s.keywords.join('\n'),
    pricingAsOf: s.pricing.asOf ?? '',
    priceTranscription: asText(s.pricing.transcriptionPerMinuteUsd),
    priceInput: asText(s.pricing.summaryInputPerMillionUsd),
    priceOutput: asText(s.pricing.summaryOutputPerMillionUsd),
  };
}

function inputOf(d: Draft): AiSettingsInput {
  return {
    enabled: d.enabled,
    summaryLanguage: d.summaryLanguage,
    transcriptionModel: d.transcriptionModel,
    summaryModel: d.summaryModel,
    retryFailedJobs: d.retryFailedJobs,
    maxConcurrentJobs: Number(d.maxConcurrentJobs),
    keywords: [...new Set(d.keywords.split('\n').map((k) => k.trim()).filter(Boolean))],
    pricingAsOf: d.pricingAsOf,
    priceTranscriptionPerMinuteUsd: asPrice(d.priceTranscription),
    priceSummaryInputPerMillionUsd: asPrice(d.priceInput),
    priceSummaryOutputPerMillionUsd: asPrice(d.priceOutput),
  };
}

export function CallsAiSettingsPanel() {
  const { t, locale } = useI18n();
  const toast = useToast();
  const queryClient = useQueryClient();
  const status = useQuery({ queryKey: ['calls', 'ai', 'server'], queryFn: callAiApi.status, retry: false });
  const settings = useQuery({ queryKey: ['calls', 'ai', 'settings'], queryFn: callAiApi.settings, retry: false });
  const [draft, setDraft] = useState<Draft | null>(null);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (settings.data) setDraft(draftOf(settings.data));
  }, [settings.data]);

  const save = useMutation({
    mutationFn: (acknowledgeNotice: boolean) => callAiApi.saveSettings({ ...inputOf(draft!), acknowledgeNotice }),
    onSuccess: () => {
      setConfirming(false);
      toast.success(t('settings.saved'));
      void queryClient.invalidateQueries({ queryKey: ['calls', 'ai'] });
    },
    onError: (e: Error) => {
      if (e instanceof ApiError && e.code === 'CONFIRMATION_REQUIRED') {
        setConfirming(true);
        return;
      }
      setConfirming(false);
      toast.error(e.message);
    },
  });

  // Not the administrators' to see: the section is not shown at all.
  if (settings.error instanceof ApiError && settings.error.status === 403) return null;
  if (settings.isLoading || status.isLoading || !draft) return settings.error ? <ErrorState error={settings.error} onRetry={() => void settings.refetch()} /> : <LoadingBlock />;
  const s = settings.data!;
  const server = status.data;
  const serverBlockers = server?.blockers.filter((b) => b !== 'SETTING_OFF') ?? [];
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => setDraft((d) => (d ? { ...d, [key]: value } : d));
  const before = draftOf(s);
  const changed = (Object.keys(before) as Array<keyof Draft>).some((k) => before[k] !== draft[k]);
  const concurrent = Number(draft.maxConcurrentJobs);
  const priceBad = [draft.priceTranscription, draft.priceInput, draft.priceOutput].some((p) => p.trim() !== '' && !(Number(p) >= 0 && Number(p) <= 1000));
  const keywordList = draft.keywords.split('\n').map((k) => k.trim()).filter(Boolean);
  const keywordsBad = keywordList.length > s.limits.maxKeywords || keywordList.some((k) => k.length > s.limits.maxKeywordLength || /[<>]/.test(k));
  const invalid = !(Number.isInteger(concurrent) && concurrent >= s.limits.minConcurrentJobs && concurrent <= s.limits.maxConcurrentJobs) || priceBad || keywordsBad;
  // Switching on asks first; switching on on a server that is not ready cannot be done at all.
  const turningOn = draft.enabled && !s.enabled;

  return (
    <>
      <Card>
        <CardHeader
          title={
            <span className="inline-flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary dark:text-primary-400" aria-hidden />
              {t('callAi.settings.title')}
            </span>
          }
          subtitle={t('callAi.settings.subtitle')}
        />
        {server && serverBlockers.length > 0 ? (
          <div className="mb-4 rounded-md border border-warning/40 bg-warning/10 p-3 text-body-sm text-[var(--text-primary)]">
            <p className="flex items-center gap-2 font-medium">
              <TriangleAlert className="h-4 w-4 text-warning-fg dark:text-warning" aria-hidden />
              {t('callAi.settings.notReady')}
            </p>
            <ul className="mt-2 list-disc space-y-1 ps-5 text-[var(--text-secondary)]">
              {serverBlockers.map((b) => (
                <li key={b}>{t(BLOCKER_LABEL[b])}</li>
              ))}
            </ul>
          </div>
        ) : (
          server && (
            <p className="mb-4 flex items-center gap-2 text-body-sm text-success-fg dark:text-success">
              <CheckCircle2 className="h-4 w-4" aria-hidden />
              {server.effective ? t('callAi.settings.running') : t('callAi.settings.ready')}
            </p>
          )
        )}
        <Checkbox
          checked={draft.enabled}
          onChange={(e) => set('enabled', e.target.checked)}
          disabled={!s.enabled && serverBlockers.length > 0}
          label={t('callAi.settings.enabled')}
        />
        <p className="mt-1 ps-6 text-caption text-faint">{t('callAi.settings.enabledHint')}</p>
        {s.firstEnabledAt && <p className="mt-2 ps-6 text-caption text-muted">{t('callAi.settings.firstEnabledAt', { date: formatDateTime(s.firstEnabledAt, locale) })}</p>}
        {server && (
          <p className="mt-3 text-caption text-faint">
            {t('callAi.settings.queue', { queued: server.queue.queued, running: server.queue.running, retrying: server.queue.retryPending, failed: server.queue.failedLastDay })}
            {server.tools.ffmpeg && <span dir="ltr"> · {server.tools.ffmpeg.split(' Copyright')[0]}</span>}
          </p>
        )}
      </Card>

      <Card>
        <CardHeader title={t('callAi.settings.howTitle')} subtitle={t('callAi.settings.howSubtitle')} />
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label={t('callAi.settings.language')} hint={t('callAi.settings.languageHint')}>
            <Select value={draft.summaryLanguage} onChange={(e) => set('summaryLanguage', e.target.value as 'ar' | 'en')}>
              {s.offered.languages.map((l) => (
                <option key={l} value={l}>
                  {t(l === 'ar' ? 'callAi.settings.arabic' : 'callAi.settings.english')}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('callAi.settings.transcriptionModel')} hint={t('callAi.settings.provider')}>
            <Select value={draft.transcriptionModel} onChange={(e) => set('transcriptionModel', e.target.value)} dir="ltr">
              {s.offered.transcription.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('callAi.settings.summaryModel')} hint={t('callAi.settings.modelsHint')}>
            <Select value={draft.summaryModel} onChange={(e) => set('summaryModel', e.target.value)} dir="ltr">
              {s.offered.summary.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <div className="sm:pt-7">
            <Checkbox checked={draft.retryFailedJobs} onChange={(e) => set('retryFailedJobs', e.target.checked)} label={t('callAi.settings.retry')} />
            <p className="mt-1 ps-6 text-caption text-faint">{t('callAi.settings.retryHint')}</p>
          </div>
          <Field
            label={t('callAi.settings.concurrent')}
            hint={t('callAi.settings.concurrentHint', { min: s.limits.minConcurrentJobs, max: s.limits.maxConcurrentJobs })}
          >
            <Input
              type="number"
              min={s.limits.minConcurrentJobs}
              max={s.limits.maxConcurrentJobs}
              step={1}
              value={draft.maxConcurrentJobs}
              onChange={(e) => set('maxConcurrentJobs', e.target.value)}
            />
          </Field>
          <Field
            label={t('callAi.settings.keywords')}
            hint={t('callAi.settings.keywordsHint', { max: s.limits.maxKeywords })}
            error={keywordsBad ? t('callAi.settings.keywordsInvalid', { max: s.limits.maxKeywords, length: s.limits.maxKeywordLength }) : null}
          >
            <Textarea value={draft.keywords} onChange={(e) => set('keywords', e.target.value)} rows={4} dir="auto" />
          </Field>
        </div>
      </Card>

      <Card>
        <CardHeader title={t('callAi.settings.pricesTitle')} subtitle={t('callAi.settings.pricesSubtitle')} />
        <div className="grid gap-4 sm:grid-cols-4">
          <Field label={t('callAi.settings.pricesAsOf')}>
            <Input type="date" value={draft.pricingAsOf} max={new Date().toISOString().slice(0, 10)} onChange={(e) => set('pricingAsOf', e.target.value)} />
          </Field>
          <Field label={t('callAi.settings.priceTranscription')} error={priceBad ? t('callAi.settings.priceInvalid') : null}>
            <Input type="number" min={0} step="any" inputMode="decimal" value={draft.priceTranscription} onChange={(e) => set('priceTranscription', e.target.value)} dir="ltr" />
          </Field>
          <Field label={t('callAi.settings.priceInput')}>
            <Input type="number" min={0} step="any" inputMode="decimal" value={draft.priceInput} onChange={(e) => set('priceInput', e.target.value)} dir="ltr" />
          </Field>
          <Field label={t('callAi.settings.priceOutput')}>
            <Input type="number" min={0} step="any" inputMode="decimal" value={draft.priceOutput} onChange={(e) => set('priceOutput', e.target.value)} dir="ltr" />
          </Field>
        </div>
      </Card>

      <div className="flex justify-end">
        <Button variant="primary" loading={save.isPending && !confirming} disabled={!changed || invalid} onClick={() => (turningOn ? setConfirming(true) : save.mutate(false))}>
          <Save className="h-4 w-4" aria-hidden />
          {t('common.save')}
        </Button>
      </div>

      <AiUsageCard />

      {confirming && (
        <ConfirmDialog
          title={t('callAi.settings.confirmTitle')}
          confirmLabel={t('callAi.settings.confirmButton')}
          busy={save.isPending}
          onConfirm={() => save.mutate(true)}
          onCancel={() => setConfirming(false)}
        >
          <p className="text-body-sm text-[var(--text-primary)]">{t('callAi.settings.confirmBody')}</p>
          <p className="mt-2 text-body-sm text-muted">{t('callAi.settings.confirmNewOnly')}</p>
        </ConfirmDialog>
      )}
    </>
  );
}

/** What the jobs of the last days used, and an estimate of what it cost — counts only, never what was said. */
function AiUsageCard() {
  const { t, locale } = useI18n();
  const [days, setDays] = useState(30);
  const usage = useQuery({ queryKey: ['calls', 'ai', 'usage', days], queryFn: () => callAiApi.usage(days), retry: false });
  const n = (v: number) => formatNumber(v, locale);
  const u = usage.data;
  // Each job's estimate was made at the prices in force when it finished; none at all without prices.
  const estimated = !!u && (u.estimatedCostUsd > 0 || !!u.pricing.asOf);
  return (
    <Card>
      <CardHeader
        title={t('callAi.usage.title')}
        subtitle={t('callAi.usage.subtitle')}
        action={
          <Select value={String(days)} onChange={(e) => setDays(Number(e.target.value))} className="w-36" aria-label={t('callAi.usage.period')}>
            {[7, 30, 90].map((d) => (
              <option key={d} value={d}>
                {t('callAi.usage.lastDays', { n: d })}
              </option>
            ))}
          </Select>
        }
      />
      {usage.isLoading ? (
        <LoadingBlock />
      ) : !u ? (
        <ErrorState error={usage.error} onRetry={() => void usage.refetch()} />
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Metric label={t('callAi.usage.summaries')} value={n(u.summaries.written)} hint={t('callAi.usage.noSpeech', { n: n(u.summaries.noSpeech) })} labelLines={2} />
            <Metric
              label={t('callAi.usage.jobs')}
              value={n(u.jobs.total)}
              hint={t('callAi.usage.jobsHint', { failed: n(u.jobs.failed), cancelled: n(u.jobs.cancelled), retries: n(u.retries) })}
              tone={u.jobs.failed > 0 ? 'warning' : 'neutral'}
              labelLines={2}
            />
            <Metric
              label={t('callAi.usage.minutes')}
              value={n(u.transcription.minutes)}
              hint={t('callAi.usage.tokens', { input: n(u.summary.inputTokens), output: n(u.summary.outputTokens) })}
              labelLines={2}
            />
            <Metric
              label={t('callAi.usage.cost')}
              value={estimated ? <bdi dir="ltr">${u.estimatedCostUsd.toFixed(2)}</bdi> : '—'}
              hint={
                u.unpricedJobs > 0
                  ? t('callAi.usage.costPartial', { n: n(u.unpricedJobs) })
                  : estimated
                    ? t('callAi.usage.costAtTheTime')
                    : t('callAi.usage.noPrices')
              }
              labelLines={2}
            />
          </div>
          <p className="mt-3 text-caption text-faint">{t('callAi.usage.estimateNote')}</p>
        </>
      )}
    </Card>
  );
}
